import { useEffect, useRef, useState } from 'react';
import { usePostHog } from 'posthog-js/react';
import useVelocityStore from 'src/stores/VelocityStore/useVelocityStore';
import { getHasSeenSurveyKey } from 'src/components/UserSurvey/utils';
import { Survey } from 'posthog-js';
import useModal from 'src/components/Modals/hooks/useModal';
import useCompleteSurveyLater from 'src/components/UserSurvey/hooks/useCompleteSurveyLater';
import useSyncedRef from 'src/hooks/utils/useSyncedRef';

const SURVEYS_POLL_INTERVAL_MS = 2000;
const MAX_POLLS = 10;

/**
 * Checks if the user should be shown a survey and shows it if they do.
 */
export default function usePostHogSurveys() {
	const posthog = usePostHog();
	const authority = useVelocityStore((s) => s.wallet?.currentPublicKeyString);
	const { show, remove, currentModal } = useModal();
	const [completeSurveyLater] = useCompleteSurveyLater();
	const pollCountRef = useRef(0);

	// Under the hood, PostHog uses feature flags to determine if a user should
	// be shown a survey. So we need to respond to feature flag changes.
	const [featureFlags, setFeatureFlags] = useState<string[]>();

	useEffect(() => {
		posthog.onFeatureFlags((flags) => {
			setFeatureFlags(flags);
		});
	}, [posthog]);

	const postHogDistinctId = posthog.get_distinct_id();

	// Necessary to also use a ref so we can be sure to have the most recent value
	// when checking async.
	const currentModalRef = useSyncedRef(currentModal);

	useEffect(() => {
		// If user has chosen to complete later, remove and don't show again.
		if (completeSurveyLater) {
			remove('user_survey');
			return;
		}

		if (
			// If PostHog hasn't identified the user yet, it won't be able to match them with a survey.
			authority !== postHogDistinctId ||
			// Similar to the above, we need loaded feature flags to match the user with a survey.
			!featureFlags ||
			// If we're already showing a modal, don't clobber it with the survey.
			!!currentModal
		) {
			return;
		}

		// Surveys are loaded async, and contrary to what the docs say, simply waiting
		// for `posthog.onFeatureFlags` to fire doesn't seem to work. So instead, we
		// poll until `isLoaded` from `getSurveys` is true, which seems to be more reliable.
		const interval = setInterval(async () => {
			pollCountRef.current++;

			if (pollCountRef.current >= MAX_POLLS) {
				clearInterval(interval);
				console.warn('[usePostHogSurveys] polling for surveys timed out');
				return;
			}

			try {
				const isLoaded = await new Promise<boolean | undefined>((res) =>
					posthog.getSurveys(
						(_, context) => res(context?.isLoaded),
						true // Forces a refetch instead of pulling from the PostHog cache.
					)
				);

				if (!isLoaded) {
					return;
				}

				clearInterval(interval);

				// Now that all surveys are loaded, we should be safe to check if there's
				// a survey targeting this user. I've seen this false-negative some times
				// but it usually works on reload.
				const activeMatchingSurveys = await new Promise<Survey[]>((res) =>
					posthog.getActiveMatchingSurveys((s) => res(s))
				);

				if (!activeMatchingSurveys.length) {
					return;
				}

				const survey = activeMatchingSurveys[0];

				// If the user has already completed or dismissed the survey, we don't want to show it again.
				const hasSeenSurvey = localStorage.getItem(
					getHasSeenSurveyKey(survey.id)
				);
				if (
					hasSeenSurvey ||
					// Double check a modal hasn't opened in the meantime (i.e. race condition).
					!!currentModalRef.current
				) {
					return;
				}

				show({ type: 'user_survey', props: { survey } });
			} catch (e) {
				console.error('[usePostHogSurveys] error fetching surveys', e);
			}
		}, SURVEYS_POLL_INTERVAL_MS);

		return () => {
			if (interval) {
				clearInterval(interval);
				pollCountRef.current = 0;
			}
		};
	}, [
		authority,
		posthog,
		postHogDistinctId,
		show,
		featureFlags,
		currentModal,
		completeSurveyLater,
	]);
}
